import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../../../context/alert';
import { useEntityQuery, useDeleteEntitiesMutation, getUrlForOpenEntity } from '../../../hooks/useEntityQuery';
import { useUserContext } from '../../../context/user';
import { AuthorResponse } from '../../../types/api/common';
import { ComplaintResponse, getComplaintFromString } from '../../../types/api/complaints';
import ModalSheet from '../../basic/ModalSheet';
import { UserHeaderCard } from '../../basic/UserHeaderCard';
import AdsInfoHeader from '../../user/Ads/components/AdsInfoHeader';
import { CommentCard } from '../../ad/Faq/components/CommentCard';
import { FeedbackStatusButton } from '../../basic/AppealStatusButton';
import { UserReviewCard } from '../../user/Profile/components/UserReviewModal/UserReviewCard';
import { isAllowAction } from '../../../services/permissions/utils';
import { updateComplaintsStatus } from '../../../services/complaints';
import { PostResponse } from '../../../types/api/forum';
import { ReviewResponse } from '../../../types/api/review';
import { CommentResponse } from '../../../types/api/comments';
import { FeedbackStatus, FeedbackStatusUpdateRequest } from '../../../types/api/feedback';
import { AdsResponse } from '../../../types/api/ads';
import { Stack, Button, Text, Spinner } from '@primer/react'
import { formatDateTime } from '../../../helpers/dates';

interface DisplayComplaintModalProps {
    complaint: ComplaintResponse
    isOpen: boolean;
    onDidBlock: () => void;
    onDidComplaintStatusChanged: () => void;
    onClose: () => void;
}

export const DisplayComplaintModal: React.FC<DisplayComplaintModalProps> = ({ complaint, isOpen, onDidBlock, onClose, onDidComplaintStatusChanged }) => {

    const { profile } = useUserContext()
    const navigate = useNavigate();
    const { setSuccess, setError } = useAlert();
    const { data, isLoading, error } = useEntityQuery(complaint.targetEntityType, complaint.targetEntityId);
    const { mutate: deleteEntities, isPending } = useDeleteEntitiesMutation();
    const [isLoadingRoute, setIsLoadingRoute] = useState(false)
    const [loadingStatus, setLoadingStatus] = useState(false);
    const [complaintStatus, setComplaintStatus] = useState<FeedbackStatus>(FeedbackStatus.NEW);

    useEffect(() => {
        setComplaintStatus(complaint.status)
    }, [complaint]);

    const handleDeleteComplaints = (complaints: ComplaintResponse[]) => {
        deleteEntities(complaints, {
            onSuccess: (data) => {
                setSuccess(`Объект жалобы заблокирован`);
                onDidBlock()
            }
        });
    };

    const complaintEntityView = () => {
        switch (complaint.targetEntityType) {
            case "ad":
                return data as AdsResponse && (
                    <div className="bg-white border border-border rounded-lg w-full">
                        <div
                            className="px-4 py-4 cursor-pointer hover:bg-gray-100"
                            onClick={() => navigate('/ads/ad?id=' + complaint.targetEntityId)}>
                            <AdsInfoHeader
                                ads={data}
                                imageSize={72}
                                nameFontSize={16}
                                isComplaintCard={true}
                            />
                        </div>
                    </div>
                )
            case "user":
            case "profile":
                return data as AuthorResponse && (
                    <div className="bg-white border border-border rounded-lg w-full mb-2">
                        <div
                            className="p-4 cursor-pointer hover:bg-gray-100"
                            onClick={() => navigate('/users/user?id=' + complaint.targetEntityId)}>
                            <UserHeaderCard
                                user={data}
                                isLarge={true}
                                isSupport={false}
                                onClick={() => { }} />
                        </div>
                    </div>
                )
            case "comment":
            case "chat":
                return data as CommentResponse && (
                    <div className="bg-white border border-border rounded-lg w-full mb-2">
                        <div
                            className="p-4 cursor-pointer hover:bg-gray-100"
                            onClick={openEntity}>
                            <CommentCard
                                comment={data}
                                post={undefined}
                            />
                        </div>
                    </div>
                )
            case "review":
                return data as ReviewResponse && (
                    <div className="bg-white border border-border rounded-lg w-full mb-2">
                        <div
                            className="p-4 cursor-pointer hover:bg-gray-100"
                            onClick={openEntity}>
                            <UserReviewCard
                                review={data}
                            />
                        </div>
                    </div>
                )
            case "post":
                return data as PostResponse && (
                    <div className="bg-white border border-border rounded-lg w-full mb-2">
                        <div
                            className="p-4 cursor-pointer hover:bg-gray-100"
                            onClick={openEntity}>
                            <CommentCard
                                comment={undefined}
                                post={data}
                            />
                        </div>
                    </div>
                )
            case "topic":
                return "На тему";
            case "settings":
                return "На настройки";
            case "system":
                return "На систему";
            default:
                return "";
        }
    }

    const openEntity = async () => {
        setIsLoadingRoute(true)
        try {
            const url = await getUrlForOpenEntity(complaint.targetEntityType, complaint.targetEntityId)
            if (url) {
                //window.open(url, '_blank')
                navigate(url)
            }
        } catch (error) {
            console.error('Error opening entity:', error)
            setError(`Не удается открыть, ${error}`)
        } finally {
            setIsLoadingRoute(false)
        }
    }

    const blockActionTitle = () => {
        switch (complaint.targetEntityType) {
            case "ad":
                return "Заблокировать объявление";
            case "user":
                return "Заблокировать пользователя";
            case "comment":
                return "Удалить комментарий";
            case "chat":
                return "Удалить комментарий";
            case "review":
                return "Удалить отзыв";
            case "post":
                return "Удалить комментарий";
            case "topic":
                return "Удалить тему";
            case "profile":
                return "Заблокировать пользователя";
            case "settings":
                return "Заблокировать";
            case "system":
                return "Заблокировать";
            default:
                return "";
        }
    }

    const isAllowBlock = () => {
        switch (complaint.targetEntityType) {
            case "ad":
                return isAllowAction(profile, 'BLOCK_ADS')
            case "user":
                return isAllowAction(profile, 'BLOCK_ADS')
            case "comment":
                return true
            case "chat":
                return true
            case "review":
                return true
            case "post":
                return true
            case "topic":
                return true
            case "profile":
                return isAllowAction(profile, 'BLOCK_ADS')
            case "settings":
                return false
            case "system":
                return false
            default:
                return false
        }
    }

    const deleteEntity = () => {
        handleDeleteComplaints([complaint])
    }

    const updateStatus = async (newStatus: FeedbackStatus) => {
        setLoadingStatus(true);

        setComplaintStatus(newStatus)
        const data: FeedbackStatusUpdateRequest = {
            ids: [complaint.id],
            status: newStatus
        }

        try {
            await updateComplaintsStatus(data);
            onDidComplaintStatusChanged();
        } catch (error) {
        } finally {
            setLoadingStatus(false);
        }
    };

    return (
        <ModalSheet
            isOpen={isOpen}
            onClose={onClose}
            title="Жалоба пользователя"
            size="middle"
            headerSize="regular"
        >
            {({ setTitle }) => (
                <Stack className='mt-[16px]'>
                    <Stack direction="horizontal" align="center" justify="space-between" gap="condensed">
                        <FeedbackStatusButton
                            isDisabled={loadingStatus}
                            currentStatus={complaintStatus}
                            onStatusChange={updateStatus}
                        />
                        <Text className='text-[14px] text-aloix-grey break-words'>
                            {formatDateTime(complaint.createdAt, ',')}
                        </Text>
                    </Stack>

                    <div className="bg-header border border-border rounded-lg w-full mb-2">
                        <div className="bg-white w-full rounded-t-lg border-b">
                            <div className="p-4">
                                <UserHeaderCard
                                    user={complaint.user}
                                    isLarge={false}
                                    isSupport={false}
                                    onClick={() => navigate('/users/user?id=' + complaint.user.id)} />
                            </div>
                        </div>

                        <div className="bg-header w-full rounded-b-lg border-b">
                            <div className="ml-[14px] mr-[14px] mb-4">
                                <Stack direction="horizontal" justify="space-between" gap="condensed">
                                    <Stack direction="horizontal" align="center" gap="condensed">
                                        <Text className='text-[14px] text-aloix-grey ml-1 mt-3 break-words'>
                                            {complaint.text}
                                        </Text>
                                    </Stack>

                                </Stack>
                            </div>
                        </div>
                    </div>
                    <Text as="span" weight='semibold' className='text-[14px] text-aloix-black'>
                        {getComplaintFromString(complaint.targetEntityType)}
                    </Text>

                    {complaintEntityView()}
                    {error && <Text as="span" weight='semibold' className='text-[14px] text-aloix-black'>
                        Не удается получить объект жалобы
                    </Text>}

                    <Stack direction="horizontal" gap='normal' justify='end'>
                        <div></div>
                        {isLoadingRoute && <Spinner />}
                        {
                            isAllowBlock() && (
                                <Button
                                    onClick={deleteEntity}
                                    size='small'
                                    loading={isPending || isLoading}
                                    variant="danger">
                                    {blockActionTitle()}
                                </Button>
                            )
                        }
                    </Stack>
                </Stack>
            )
            }
        </ModalSheet>
    );
};